import type { TErrorBody } from '@cascade/api-client/errors';
import {
  RequestError,
  isRetryableStatus,
  statusByErrorCode,
} from '#errors/index.ts';
import { isEnvelope, retryAfterOf } from './connection.helpers.ts';
import type { TConnectionTimeout, TOnRequest } from './connection.types.ts';
import { isKeyOf } from '#utils/object/index.ts';

export const DEFAULT_TIMEOUT = 65_000;

const envelopeOf = async (
  response: Response,
): Promise<TErrorBody | undefined> => {
  try {
    const data: unknown = await response.json();

    return isEnvelope(data) ? data.error : undefined;
  } catch {
    return undefined;
  }
};

export const withSessionCookie: TOnRequest = (init) => ({
  ...init,
  credentials: 'include',
});

export const withTimeout = (
  signal: AbortSignal | undefined,
  timeout: number,
): TConnectionTimeout => {
  const controller = new AbortController();
  const forward = () => controller.abort(signal?.reason);

  const timer = setTimeout(
    () => controller.abort(new DOMException('timed out', 'TimeoutError')),
    timeout,
  );

  if (signal?.aborted) {
    forward();
  } else {
    signal?.addEventListener('abort', forward, { once: true });
  }

  return {
    signal: controller.signal,
    clear: () => clearTimeout(timer),
    release: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', forward);
    },
  };
};

export const throwConnectionError = (cause: unknown): never => {
  if (cause instanceof RequestError) {
    throw cause;
  }

  if (cause instanceof DOMException && cause.name === 'AbortError') {
    throw new RequestError({ kind: 'aborted', retryable: false }, { cause });
  }

  if (cause instanceof DOMException && cause.name === 'TimeoutError') {
    throw new RequestError({ kind: 'timeout', retryable: true }, { cause });
  }

  if (cause instanceof TypeError) {
    throw new RequestError({ kind: 'network', retryable: true }, { cause });
  }

  throw cause;
};

export const throwResponseError = async (
  response: Response,
): Promise<never> => {
  throw new RequestError({
    kind: 'server',
    retryable: isRetryableStatus(response.status),
    status: response.status,
    body: await envelopeOf(response),
    retryAfter: retryAfterOf(response),
  });
};

export const throwEventError = (data: string): never => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(data);
  } catch (cause) {
    throw new RequestError({ kind: 'decode', retryable: false }, { cause });
  }

  if (!isEnvelope(parsed)) {
    throw new RequestError({ kind: 'decode', retryable: false });
  }

  if (!isKeyOf(statusByErrorCode, parsed.error.code)) {
    throw new RequestError({ kind: 'decode', retryable: false });
  }

  const status = statusByErrorCode[parsed.error.code];

  throw new RequestError({
    kind: 'server',
    retryable: isRetryableStatus(status),
    status,
    body: parsed.error,
  });
};
